import React, {useEffect, useState} from "react";
import {
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TablePagination,
	TextField,
	Typography,
	Chip,
	Toolbar,
	Button,
	CircularProgress,
	Box,
	Tooltip,
	IconButton
} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import API from "../../../network/dashboardAPI";
import DelipointPromotionDialog from "./component/DelipointPromotionDialog";
import ActionButtons from "./component/DeleteDialog";
import useAlert from "../../../services/useAlert";

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
		backgroundColor: "#f9fafc",
		minHeight: "100vh"
	},
	paper: {
		borderRadius: 12,
		overflow: "hidden",
		boxShadow: "0 4px 20px rgba(0,0,0,0.05)"
	},
	toolbar: {
		display: "flex",
		justifyContent: "space-between",
		padding: theme.spacing(2)
	},
	searchBox: {
		display: "flex",
		alignItems: "center",
		padding: "2px 8px",
		borderRadius: 8,
		backgroundColor: "#fff",
		border: "1px solid #ddd",
		width: 300
	},
	searchInput: {
		marginLeft: theme.spacing(1),
		flex: 1
	},
	statusChip: {
		fontWeight: "bold"
	},
	loadingBox: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		padding: theme.spacing(3)
	},
	actionSection: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1)
	},
	targetChip: {
		margin: "2px"
	}
}));

const formatDate = (dateStr) => {
	if (!dateStr) return "-";
	return new Date(dateStr).toLocaleDateString("en-GB", {
		day: "2-digit",
		month: "short",
		year: "numeric"
	});
};

const truncateText = (value, maxLength = 30) => {
	if (!value) return "-";
	const text = String(value);
	return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

export default function DelipointPromotionsList() {
	const classes = useStyles();
	const alert = useAlert();

	const [data, setData] = useState([]);
	const [total, setTotal] = useState(0);
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [orderBy, setOrderBy] = useState("id");
	const [order, setOrder] = useState("asc");
	const [loading, setLoading] = useState(false);

	const [dialogOpen, setDialogOpen] = useState(false);
	const [selectedItem, setSelectedItem] = useState(null);

	const fetchDelipoints = async () => {
		try {
			setLoading(true);
			const params = {
				search,
				page,
				limit: rowsPerPage,
				sortField: orderBy,
				sortOrder: order,
				_locale: "en"
			};

			const response = await API.getDelipointPromotionsList(params);
			const res = await response.json();

			if (res.success) {
				setData(res.data || []);
				setTotal(res.total || 0);
				if ((res.total || 0) === 0 && search) {
					alert.toast("No promotions found for your search", "info");
				}
			} else {
				alert.error("Failed to Load", res.message || "Unable to fetch delipoint promotions");
			}
		} catch (err) {
			console.error("Error fetching delipoint promotions:", err);
			alert.error("Network Error", "Failed to load promotions. Please check your connection.");
		} finally {
			setLoading(false);
		}
	};

	const handleSearch = (e) => {
		setSearch(e.target.value);
		setPage(0);
	};

	const handleSearchSubmit = (e) => {
		if (e.key === "Enter") fetchDelipoints();
	};

	const handleAdd = () => {
		setSelectedItem(null);
		setDialogOpen(true);
	};

	const handleEdit = (item) => {
		setSelectedItem(item);
		setDialogOpen(true);
	};

	const handleDialogSaved = (action, itemName) => {
		fetchDelipoints();
		if (action === "edit") {
			alert.success("Promotion Updated", `"${itemName}" has been updated successfully.`);
		} else if (action === "create") {
			alert.success("Promotion Created", `"${itemName}" has been created successfully.`);
		}
	};

	// Debounced search
	useEffect(() => {
		const timer = setTimeout(() => {
			fetchDelipoints();
		}, 500);
		return () => clearTimeout(timer);
	}, [search, page, rowsPerPage, orderBy, order]);

	// Initial load
	useEffect(() => {
		fetchDelipoints();
	}, []);

	const isActive = (row) => {
		if (row.is_deleted) return false;
		if (row.is_active === false) return false;
		return true;
	};

	return (
		<div className={classes.root}>
			<Typography variant="h5" gutterBottom style={{display: "flex", alignItems: "center", gap: 8}}>
				Delipoint Promotions
				{loading && <CircularProgress size={20}/>}
			</Typography>

			<Paper className={classes.paper}>
				<Toolbar className={classes.toolbar}>
					<div className={classes.searchBox}>
						<SearchIcon color="action"/>
						<TextField
							placeholder="Search promotions..."
							value={search}
							onChange={handleSearch}
							onKeyPress={handleSearchSubmit}
							className={classes.searchInput}
							InputProps={{disableUnderline: true}}
						/>
					</div>
					<div className={classes.actionSection}>
						<Button
							variant="contained"
							color="primary"
							startIcon={<AddIcon/>}
							onClick={handleAdd}
						>
							Add Promotion
						</Button>
					</div>
				</Toolbar>

				<TableContainer>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>#</TableCell>
								<TableCell>Display Text</TableCell>
								<TableCell>Description</TableCell>
								<TableCell>Start Date</TableCell>
								<TableCell>End Date</TableCell>
								<TableCell>Min. Order ($)</TableCell>
								<TableCell>Fixed Points</TableCell>
								<TableCell>Multiplier</TableCell>
								<TableCell>Targets</TableCell>
								<TableCell>Status</TableCell>
								<TableCell align="right">Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{loading && data.length === 0 ? (
								<TableRow>
									<TableCell colSpan={10} align="center">
										<Box className={classes.loadingBox}>
											<CircularProgress/>
											<Typography variant="body2" style={{marginLeft: 16}}>
												Loading promotions...
											</Typography>
										</Box>
									</TableCell>
								</TableRow>
							) : data.length === 0 ? (
								<TableRow>
									<TableCell colSpan={10} align="center" style={{padding: 40}}>
										<Typography variant="h6" color="textSecondary" gutterBottom>
											No promotions found
										</Typography>
										{search && (
											<Typography variant="body2" color="textSecondary">
												Try adjusting your search terms
											</Typography>
										)}
									</TableCell>
								</TableRow>
							) : (
								data.map((row, index) => (
									<TableRow key={row._id} hover>
										<TableCell>{row._id}</TableCell>
										<TableCell>
											<Typography variant="body2" style={{fontWeight: 500}}>
												{row.display_text || "-"}
											</Typography>
											{row.display_text_kh && (
												<Typography variant="caption" color="textSecondary">
													{row.display_text_kh}
												</Typography>
											)}
										</TableCell>
										<TableCell>
											<Typography>
												{truncateText(row.description, 20)}
											</Typography>
										</TableCell>
										<TableCell>{formatDate(row.start_date)}</TableCell>
										<TableCell>{formatDate(row.end_date)}</TableCell>
										<TableCell>${row.minimum_order_value ?? "-"}</TableCell>
										<TableCell>
											{row.fixed_points ?? "-"}
											{row.fixed_points_multiple_use && (
												<Chip
													label="Multi-use"
													size="small"
													style={{marginLeft: 4, fontSize: 10}}
												/>
											)}
										</TableCell>
										<TableCell>{row.points_multiplier ? `×${row.points_multiplier}` : "-"}</TableCell>
										<TableCell>
											{(row.targets || []).length === 0 ? (
												<Typography variant="caption" color="textSecondary">All</Typography>
											) : (
												(row.targets || []).slice(0, 2).map((t, i) => {
													const resolvedName = t.resolved_target?.name || t.resolved_target?.first_name;
													const displayLabel = resolvedName
														? `${t.criterial_type}: ${resolvedName}`
														: `${t.criterial_type}: ${t.selection_value ?? t.condition}`;
													const tooltipText = resolvedName
														? `${t.criterial_type} ${t.condition} ${resolvedName} (${t.selection_value})`
														: `${t.criterial_type} ${t.condition} ${t.selection_value ?? ''}`;
													return (
														<Tooltip key={i} title={tooltipText}>
															<Chip
																label={displayLabel}
																size="small"
																className={classes.targetChip}
															/>
														</Tooltip>
													);
												})
											)}
											{(row.targets || []).length > 2 && (
												<Chip
													label={`+${row.targets.length - 2} more`}
													size="small"
													className={classes.targetChip}
												/>
											)}
										</TableCell>
										<TableCell>
											<Chip
												label={isActive(row) ? "Active" : "Inactive"}
												size="small"
												style={{
													backgroundColor: isActive(row) ? "#e6f4ea" : "#fce8e6",
													color: isActive(row) ? "#1e7e34" : "#c0392b",
													fontWeight: 600
												}}
											/>
										</TableCell>
										<TableCell align="right">
											<ActionButtons
												row={row}
												handleEdit={handleEdit}
											/>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>

				<TablePagination
					component="div"
					count={total}
					page={page}
					onPageChange={(e, newPage) => setPage(newPage)}
					rowsPerPage={rowsPerPage}
					onRowsPerPageChange={e => {
						setRowsPerPage(parseInt(e.target.value, 10));
						setPage(0);
					}}
					rowsPerPageOptions={[5, 10, 25, 50]}
				/>
			</Paper>

			<DelipointPromotionDialog
				open={dialogOpen}
				onClose={() => setDialogOpen(false)}
				onSaved={handleDialogSaved}
				initialData={selectedItem}
			/>
		</div>
	);
}